/**
 * WhatsAppInboxPanel — Dispatcher Inbox (Phase 1, WhatsApp channel).
 *
 * A read-mostly inbox for inbound WhatsApp conversations with dispatcher
 * replies. Sandbox/test-safe: when the WhatsApp sender isn't live, replies are
 * logged + audited but NOT delivered (a clear banner says so). Channel is
 * always clearly labelled "WhatsApp" so SMS can later be folded in.
 *
 * Endpoints: /api/admin/whatsapp/{status,conversations,conversations/{id},
 * conversations/{id}/reply}. No AI bot, no auto ride creation, no templates.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  MessageSquare, Send, RefreshCw, ShieldAlert, Clock, CheckCheck,
} from "lucide-react";
import { AiSettingsCard, AttentionList, ConversationAiCard } from "./WhatsAppAiControls";

const fmtTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const ChannelBadge = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-[#DCFCE7] text-[#166534]" data-testid="whatsapp-channel-badge">
    <MessageSquare className="w-2.5 h-2.5" /> WhatsApp
  </span>
);

export function WhatsAppInboxPanel() {
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // wa_id
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const threadEndRef = useRef(null);
  const [attentionIds, setAttentionIds] = useState(new Set());

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whatsapp/conversations");
      setConversations(data.conversations || []);
      setStatus(data.status || null);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  const loadThread = useCallback(async (waId) => {
    try {
      const { data } = await api.get(`/admin/whatsapp/conversations/${encodeURIComponent(waId)}`);
      setThread(data);
      loadList(); // refresh unread counts after marking read
    } catch (e) { toast.error(formatApiError(e) || "Failed to load conversation"); }
  }, [loadList]);

  useEffect(() => {
    loadList();
    const t = setInterval(loadList, 15000);
    return () => clearInterval(t);
  }, [loadList]);

  useEffect(() => {
    if (!active) return;
    loadThread(active);
    const t = setInterval(() => loadThread(active), 15000);
    return () => clearInterval(t);
  }, [active, loadThread]);

  useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/whatsapp/conversations/${encodeURIComponent(active)}/reply`, { body });
      setReply("");
      if (data.status === "sent") toast.success("Reply sent");
      else if (data.status?.startsWith("skipped")) toast.info("Logged (sandbox — sender not live yet)");
      else if (data.status === "failed") toast.error("Reply failed to send");
      loadThread(active);
    } catch (e) { toast.error(formatApiError(e) || "Failed to send reply"); }
    finally { setSending(false); }
  };

  const isSandbox = status && status.mode !== "live";

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="whatsapp-inbox-panel">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-6 h-6 text-[#166534]" />
        <div>
          <h2 className="font-heading text-2xl font-black tracking-tight">Dispatcher Inbox</h2>
          <div className="text-xs text-muted-foreground">Inbound WhatsApp conversations · replies within the 24h window</div>
        </div>
      </div>

      {/* Channel status banner */}
      {status && (
        <div
          className={`flex items-start gap-2 border px-4 py-2.5 text-sm ${isSandbox ? "border-[#FDE68A] bg-[#FFFBEB]" : "border-[#A7F3D0] bg-[#ECFDF5]"}`}
          data-testid="whatsapp-status-banner"
        >
          <ShieldAlert className={`w-4 h-4 mt-0.5 ${isSandbox ? "text-[#92400E]" : "text-[#065F46]"}`} />
          <div>
            {isSandbox ? (
              <>
                <span className="font-bold">Sandbox / Test mode.</span>{" "}
                <span className="text-muted-foreground">Inbound messages are captured; replies are logged &amp; audited but <strong>not delivered</strong> until the WhatsApp sender is live and enabled.</span>
              </>
            ) : (
              <>
                <span className="font-bold">Live.</span>{" "}
                <span className="text-muted-foreground">Sending from {status.sender}.</span>
              </>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <AiSettingsCard />
        <AttentionList onOpen={setActive} onLoaded={setAttentionIds} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[60vh]">
        {/* Conversation list */}
        <div className="bg-white border border-border overflow-y-auto max-h-[70vh]" data-testid="whatsapp-conversation-list">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground italic" data-testid="whatsapp-inbox-empty">
              No WhatsApp conversations yet. Inbound messages will appear here.
            </div>
          ) : conversations.map((c) => (
            <button
              key={c.wa_id}
              onClick={() => setActive(c.wa_id)}
              className={`w-full text-left px-3 py-3 border-b border-border hover:bg-secondary/50 ${active === c.wa_id ? "bg-secondary" : ""}`}
              data-testid={`whatsapp-conv-${c.wa_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm truncate">{c.profile_name || c.wa_id}</span>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtTime(c.last_ts)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className="text-xs text-muted-foreground truncate">
                  {c.last_direction === "outbound" && <CheckCheck className="w-3 h-3 inline mr-1 text-[#166534]" />}
                  {c.last_body || "—"}
                </span>
                {c.unread > 0 && (
                  <span className="bg-[#166534] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid={`whatsapp-conv-unread-${c.wa_id}`}>{c.unread}</span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-1">
                <ChannelBadge />
                {attentionIds.has(c.wa_id) && (
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-[#FEF3C7] text-[#92400E]" data-testid={`whatsapp-conv-attention-${c.wa_id}`}>Needs attention</span>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Thread + reply */}
        <div className="lg:col-span-2 bg-white border border-border flex flex-col max-h-[70vh]" data-testid="whatsapp-thread">
          {!active || !thread ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Select a conversation to view messages.
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
                <div>
                  <div className="font-bold flex items-center gap-2">{thread.profile_name || thread.wa_id} <ChannelBadge /></div>
                  <div className="text-xs text-muted-foreground">{thread.wa_id}</div>
                </div>
                <div className={`text-[11px] font-bold inline-flex items-center gap-1 ${thread.within_session_window ? "text-[#065F46]" : "text-[#92400E]"}`} data-testid="whatsapp-session-window">
                  <Clock className="w-3 h-3" /> {thread.within_session_window ? "Within 24h window" : "Outside 24h window"}
                </div>
              </div>

              <ConversationAiCard waId={active} />

              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {thread.messages.map((m) => (
                  <div
                    key={m.id}
                    className={`max-w-[75%] px-3 py-2 text-sm rounded-lg ${m.direction === "inbound" ? "bg-secondary mr-auto" : "bg-[#DCFCE7] ml-auto"}`}
                    data-testid={`whatsapp-message-${m.id}`}
                  >
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1 justify-end">
                      {m.author === "ai" && <span className="font-bold text-[#166534]" data-testid={`whatsapp-message-ai-${m.id}`}>AI ·</span>}
                      {fmtTime(m.ts)}
                      {m.status === "mock_not_sent" && <span className="italic">· not sent (preview)</span>}
                      {m.status === "blocked_sender_not_allowed" && <span className="text-[#EF4444] italic">· blocked (sender not allowed)</span>}
                      {m.direction === "outbound" && m.status?.startsWith("skipped") && <span className="italic">· logged (sandbox)</span>}
                      {m.direction === "outbound" && m.status === "failed" && <span className="text-[#EF4444] italic">· failed</span>}
                      {m.direction === "outbound" && m.status === "sent" && <CheckCheck className="w-3 h-3 text-[#166534]" />}
                    </div>
                  </div>
                ))}
                <div ref={threadEndRef} />
              </div>

              {!thread.within_session_window && (
                <div className="px-4 py-1.5 text-[11px] text-[#92400E] bg-[#FFFBEB] border-t border-[#FDE68A]">
                  Outside the 24h window — freeform replies may be rejected by WhatsApp (templates not enabled in Phase 1).
                </div>
              )}

              <div className="p-3 border-t border-border flex items-center gap-2">
                <Input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder={isSandbox ? "Type a reply (sandbox — will be logged, not sent)…" : "Type a reply…"}
                  data-testid="whatsapp-reply-input"
                />
                <Button onClick={sendReply} disabled={sending || !reply.trim()} className="bg-[#166534] hover:bg-[#14532D] text-white" data-testid="whatsapp-reply-send">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default WhatsAppInboxPanel;
